<template>
	<section class="ts-space-y-4">
		<h2 class="ts-font-semibold ts-text-slate-700 dark:ts-text-slate-200 ts-flex ts-items-center ts-gap-2">
			<span class="material-icons ts-text-sm">list_alt</span>
			{{ $t('invoiceAdjustment.invoiceDetailItems') }}
		</h2>
		<div
			class="ts-bg-white dark:ts-bg-slate-800 ts-border ts-border-slate-200 ts-rounded-xl ts-overflow-hidden ts-shadow-sm">
			<div class="ts-overflow-x-auto custom-scrollbar">
				<table class="ts-w-full ts-text-left ts-border-collapse ts-min-w-[1200px]">
					<thead>
						<tr
							class="ts-bg-slate-50 dark:ts-bg-slate-900 ts-text-[11px] ts-font-bold ts-uppercase ts-tracking-wider ts-text-slate-500 dark:ts-text-slate-400 ts-border-b ts-border-slate-200 dark:ts-border-slate-700">
							<th class="ts-px-2 ts-py-3 ts-w-12 ts-text-center">{{ $t('invoiceAdjustment.no') }}</th>
							<th class="ts-px-2 ts-py-3 ts-w-48">{{ $t('invoiceAdjustment.feeType') }}</th>
							<th class="ts-px-2 ts-py-3 ts-w-40">{{ $t('invoiceAdjustment.oldFee') }}</th>
							<th class="ts-px-2 ts-py-3 ts-w-48">{{ $t('invoiceAdjustment.newFee') }} <span class="ts-text-red-500">*</span> </th>
							<th class="ts-px-2 ts-py-3 ts-min-w-[200px]">{{ $t('invoiceAdjustment.description') }}</th>
							<th class="ts-px-2 ts-py-3 ts-w-24">{{ $t('invoiceAdjustment.quantity') }} <span class="ts-text-red-500">*</span></th>
							<th class="ts-px-2 ts-py-3 ts-w-32">{{ $t('invoiceAdjustment.fee') }}</th>
							<th class="ts-px-2 ts-py-3 ts-w-40">{{ $t('invoiceAdjustment.startDate') }}</th>
							<th class="ts-px-2 ts-py-3 ts-w-40">{{ $t('invoiceAdjustment.endDate') }}</th>
							<th class="ts-px-2 ts-py-3 ts-w-32 ts-text-right ts-whitespace-no-wrap">{{
								$t('invoiceAdjustment.amount')
							}} ({{ defaultCurrency ? defaultCurrency.currency_symbol : '' }})</th>
						</tr>
					</thead>
					<tbody class="ts-divide-y ts-divide-slate-100 dark:ts-divide-slate-700">
						<!-- Empty State -->
						<tr v-if="!model.inv_detail_list || model.inv_detail_list.length === 0">
							<td colspan="10" class="ts-px-4 ts-py-12">
								<div class="ts-text-center ts-space-y-3">
									<span class="material-icons ts-text-slate-300 dark:ts-text-slate-600 ts-text-5xl">receipt_long</span>
									<p class="ts-text-sm ts-font-medium ts-text-slate-500 dark:ts-text-slate-400">{{
										$t('invoiceAdjustment.noInvoiceItems') }}</p>
									<p class="ts-text-xs ts-text-slate-400 dark:ts-text-slate-500">{{
										$t('invoiceAdjustment.noInvoiceItemsDescription') }}</p>
								</div>
							</td>
						</tr>
						<!-- Invoice Items -->
						<tr v-for="(item, index) in model.inv_detail_list" :key="index"
							class="hover:ts-bg-slate-50 dark:hover:ts-bg-slate-800/50 ts-transition-colors">
							<td class="ts-px-2 ts-py-1 ts-text-sm ts-text-slate-500 ts-text-center">{{ index + 1 }}</td>
							<td class="ts-px-2 ts-py-1">
								<a-select v-model="item.fee_type_id" :options="feeTypes" disabled class="ts-w-full"></a-select>
							</td>
							<td class="ts-px-2 ts-py-1">
								<div
									class="ts-bg-slate-100 dark:ts-bg-slate-800 ts-px-3 ts-py-2 ts-rounded ts-text-xs ts-text-slate-500 ts-border ts-border-transparent">
									{{ item.old_fee_setup_name }}
								</div>
							</td>
							<td class="ts-px-2 ts-py-1">
								<a-select v-model="item.fee_setup_id"  :options="getFeeItem(item.fee_type_id)"
									:showSearch="true"
									:filterOption="filterOption"
									 @change="(_, prop) => onFeeSetupChange(item, prop)"
									class="ts-w-full"
									></a-select>
									<small
										 v-if="errors.has('inv_detail_list.' + index + '.fee_setup_id')"
										 class="ts-text-red-500"
									>{{ errors.first('inv_detail_list.' + index + '.fee_setup_id') }}</small>
							</td>
							<td class="ts-px-2 ts-py-1">
								<a-input v-model="item.fee_description" class="ts-w-full" type="text" />
							</td>
							<td class="ts-px-2 ts-py-1">
								<a-input v-model.number="item.quantity" class="ts-w-full" type="number"
									:class="{
										'ts-border-red-500': errors.has('inv_detail_list.' + index + '.quantity')
									}"
								/>
							</td>
							<td class="ts-px-2 ts-py-1 ts-text-xs ts-font-medium ts-text-slate-600 dark:ts-text-slate-300">
								{{ defaultCurrency ? defaultCurrency.currency_symbol : '' }}
								{{ item.fee_amount }}</td>
							<td class="ts-px-2 ts-py-1">
								<a-date-picker v-model="item.fee_start_date" placeholder="Date To" valueFormat="DD-MM-YYYY"
									format="DD-MM-YYYY" style="width: 100%" />
							</td>
							<td class="ts-px-2 ts-py-1">

								<a-date-picker v-model="item.fee_end_date" placeholder="Date To" valueFormat="DD-MM-YYYY"
									format="DD-MM-YYYY" style="width: 100%" />
							</td>
							<td
								class="ts-px-2 ts-py-1 ts-text-right ts-text-sm ts-font-bold ts-text-slate-700 dark:ts-text-slate-200">
								{{ defaultCurrency ? defaultCurrency.currency_symbol : '' }}
								{{ calculateAmount(item) }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</section>
</template>

<script>
import { sumBy } from 'lodash';
import { mapState } from 'vuex';
import { Errors } from 'form-backend-validation';

export default {
	name: "InvoiceDetailTable",
	props: {
		value: {},
		feeItems: {
			type: Object,
			default: () => { }
		},
		errors: {
			type: Errors,
			default: () => new Errors()
		}
	},
	data() {
		return {
			model: {}
		}
	},
	created() {
		this.model = this.value
	},
	computed: {
		...mapState('invoiceAjustment', ['feeTypes', 'currencies']),
		hasReceivePayment() {
			return Object.keys(this.model.received_payment).length
		},
		defaultCurrency() {
			return this.currencies.find(c => c.is_default)
		},
		getFeeItem: vm => feeTypeId => {
			if (feeTypeId != undefined) {
				let item = vm.feeItems[feeTypeId]
				if (item) {
					return item.map(el => ({
						value: el.fee_setup_id,
						label: el.fee_name,
						obj: el
					}))
				}
				return []
			}
			return []
		},
	},
	methods: {
		calculateAmount(item) {
			return (item.quantity * item.fee_amount).toFixed(2)
		},
		filterOption(input, option) {
			return option.componentOptions.children[0].text
				.toLowerCase()
				.indexOf(input.toLowerCase()) >= 0
		},
		calculate() {
			let invoiceAmount = sumBy(this.model.inv_detail_list, o => {
				return o.quantity * o.fee_amount
			})
			this.model.new_inv_amount = invoiceAmount
		},
		onFeeSetupChange(detail, prop) {
			let item = prop.data.props.obj
			detail.fee_setup_id = item.fee_setup_id
			detail.fee_description = item.fee_name
			detail.fee_start_date = item.fee_start_date
			detail.fee_end_date = item.fee_end_date
			detail.payment_term_id = parseInt(item.payment_term_id)
			detail.duration_in_month = parseInt(item.duration_in_month)
			detail.fee_amount = parseFloat(item.fee_amount)
			detail.ac_term_id = parseInt(item.ac_term_id)
			detail.grade_id = parseInt(item.grade_id)
			detail.shift_id = parseInt(item.shift_id)
			detail.payment_term_name = item.payment_term_name
			detail.pro_type_id = parseInt(item.pro_type_id)
			detail.fee_description = item.fee_name
			this.calculate()
		},
	},

}
</script>
